import React, { useEffect, useState } from "react";
import { Button, Form, notification, Spin } from "antd";
import { Link, useNavigate, useParams } from "react-router-dom";
import B2C2BApi from "../../api-services/b2c2b-api.service";
import BookingSummaryPanel from "./b2c2b/payment/BookingSummaryPanel";
import PaymentMethodPanel from "./b2c2b/payment/PaymentMethodPanel";
import PaymentSummarySidebar from "./b2c2b/payment/PaymentSummarySidebar";
import PaymentTermsPanel from "./b2c2b/payment/PaymentTermsPanel";
import PriceBreakdownModal from "./b2c2b/payment/PriceBreakdownModal";
import { usePaymentAmounts } from "./b2c2b/payment/usePaymentAmounts";
import { FareType, PaymentChannel, PayMode } from "./b2c2b/payment/types";

function DedicatedPaymentPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [loading, setLoading]                       = useState(true);
  const [submitting, setSubmitting]                 = useState(false);
  const [paymentInfo, setPaymentInfo]               = useState<any>(null);
  const [allPaymentMethods, setAllPaymentMethods]   = useState<any>({});
  const [selectedChannel, setSelectedChannel]       = useState<PaymentChannel | null>(null);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<any[]>([]);
  const [fareType, setFareType]                     = useState<FareType>("customer_full_fare");
  const [payMode, setPayMode]                       = useState<PayMode>("remaining_balance");
  const [selectedAddonIds, setSelectedAddonIds]     = useState<number[]>([]);
  const [breakdownOpen, setBreakdownOpen]           = useState(false);

  const amounts = usePaymentAmounts(paymentInfo, { fareType, payMode, selectedAddonIds });

  useEffect(() => {
    Promise.all([
      B2C2BApi.getPaymentInfo(Number(id)),
      B2C2BApi.getAllPaymentMethods(),
    ])
      .then(([info, methods]) => {
        setPaymentInfo(info);
        setAllPaymentMethods(methods);
        if (info?.error) {
          notification.error({ message: info.error });
        } else if (info?.allow_customer_pay_deposit && !info?.deposit_covered) {
          setFareType("customer_deposit");
        }
      })
      .catch(() => notification.error({ message: "Failed to load payment details." }))
      .finally(() => setLoading(false));
  }, [id]);

  const handleChannelSelect = (channel: PaymentChannel) => {
    form.setFieldValue("payment_method", undefined);
    setSelectedChannel(channel);
    const options = (allPaymentMethods?.[channel.bankName] || []).map((item: any) => {
      const [label, value] = item;
      return {
        value,
        label,
        disabled: typeof label === "string" && label.includes("(Offline)"),
      };
    });
    setPaymentMethodOptions(options);
  };

  const changePayMode = (mode: PayMode) => {
    setPayMode(mode);
    if (mode === "remaining_balance") setSelectedAddonIds([]);
  };

  const toggleAddon = (addonId: number, checked: boolean) => {
    setSelectedAddonIds((ids) => (checked ? [...ids, addonId] : ids.filter((existing) => existing !== addonId)));
  };

  const toggleAllAddons = (checked: boolean) => {
    setSelectedAddonIds(checked ? amounts.selectableAddons.map((addon) => addon.id) : []);
  };

  const handleSubmit = (values: any) => {
    if (!selectedChannel) {
      notification.warning({ message: "Please select a payment method." });
      return;
    }
    if (amounts.payingAddonsOnly && selectedAddonIds.length === 0) {
      notification.warning({ message: "Please select at least one add-on to pay." });
      return;
    }
    setSubmitting(true);
    B2C2BApi.createPayment(Number(id), {
      payment: {
        gateway: selectedChannel.value,
        method: values.payment_method || "",
        bank_type: "",
        redirect_url: "",
      },
      fare_type_selected: fareType,
      ...(amounts.payingAddonsOnly ? { balance_revision_ids: selectedAddonIds } : {}),
    })
      .then((response: any) => {
        if (response.error) {
          notification.error({ message: response.error });
        } else if (response.redirect_to) {
          window.location.href = response.redirect_to;
        } else {
          notification.error({ message: "No redirect URL was provided by the server." });
        }
      })
      .catch(() => notification.error({ message: "Payment could not be processed. Please try again." }))
      .finally(() => setSubmitting(false));
  };

  if (!paymentInfo && !loading) return null;

  const ccRate = paymentInfo?.cc_rate ? `${(paymentInfo.cc_rate * 100).toFixed(1)}%` : null;

  return (
    <div id="dedicated-payment-page" className="bookings-page b2c2b-section">
      {submitting && <Spin spinning fullscreen />}

      <div className="bookings-page_header" style={{ marginBottom: 4 }}>
        <h1>Complete your payment</h1>
      </div>
      {paymentInfo && (
        <p style={{ marginBottom: 10, color: "#8c8c8c" }}>
          Order <Link to={`/web/bookings/${id}`} style={{ color: "#DC1A22", fontWeight: 600 }}>{paymentInfo.order_no}</Link>
        </p>
      )}

      {loading ? <Spin spinning /> : paymentInfo?.error ? null : (
      <div className="payment-layout">
        {/* LEFT — main content */}
        <div className="payment-layout_main">
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <BookingSummaryPanel paymentInfo={paymentInfo} />

            {amounts.showPaymentTerms && (
              <PaymentTermsPanel
                paymentInfo={paymentInfo}
                amounts={amounts}
                fareType={fareType}
                onFareTypeChange={setFareType}
                payMode={payMode}
                onPayModeChange={changePayMode}
                selectedAddonIds={selectedAddonIds}
                onToggleAddon={toggleAddon}
                onToggleAllAddons={toggleAllAddons}
                onViewBreakdown={() => setBreakdownOpen(true)}
              />
            )}

            <PaymentMethodPanel
              paymentInfo={paymentInfo}
              selectedChannel={selectedChannel}
              onSelectChannel={handleChannelSelect}
              paymentMethodOptions={paymentMethodOptions}
              ccRate={ccRate}
            />

            <div style={{ marginBottom: 32 }}>
              <Button onClick={() => navigate(-1)}>← Back</Button>
            </div>
          </Form>
        </div>

        {/* RIGHT — summary sidebar */}
        <PaymentSummarySidebar
          paymentInfo={paymentInfo}
          amounts={amounts}
          submitting={submitting}
          onPayNow={() => form.submit()}
        />
      </div>
      )}

      <PriceBreakdownModal
        open={breakdownOpen}
        onClose={() => setBreakdownOpen(false)}
        money={amounts.money}
        addons={amounts.addons}
        balanceExAddons={amounts.balanceExAddons}
        currBalanceExAddons={amounts.currBalanceExAddons}
        balanceAmount={amounts.balanceAmount}
        currBalanceAmount={amounts.currBalanceAmount}
      />
    </div>
  );
}

export default DedicatedPaymentPage;
